import { useEffect, useState } from 'react'

import { ApiError } from '@/lib/api'
import { hydrateIdentity, useAuth } from '@/lib/auth'
import { IdentityBrowserEvents, useRefreshOnEvent } from '@/lib/browser-events'
import { useSelectedEntity } from '@/modules/hierarchy'

export function useEntityContextSync(): boolean {
  const { isAuthenticated, user } = useAuth()
  const { entityId, entity, ancestors } = useSelectedEntity()

  const scopedEntityId = user?.entityId

  const refreshPermissions = async () => {
    if (entityId == null) return
    await hydrateIdentity(entityId)
    if (entity.isError) await Promise.all([entity.refetch(), ancestors.refetch()])
  }

  useRefreshOnEvent(IdentityBrowserEvents.permissionsChanged, refreshPermissions)

  useEffect(() => {
    if (isAuthenticated && entityId != null && scopedEntityId !== entityId) {
      void hydrateIdentity(entityId)
    }
  }, [isAuthenticated, entityId, scopedEntityId])

  const error = entity.error
  const scopedIdentityReady = user?.entityId === entityId
  const hasScopedPermissions = scopedIdentityReady && (user.permissions?.length ?? 0) > 0

  // The user belongs on a custom domain. Replace, so Back does not move again.
  const portalOrigin = scopedIdentityReady ? user.portalOrigin : undefined
  useEffect(() => {
    if (portalOrigin === undefined || portalOrigin === window.location.origin) return
    const { pathname, search, hash } = window.location
    window.location.replace(new URL(pathname + search + hash, portalOrigin))
  }, [portalOrigin])

  const unavailable =
    error instanceof ApiError &&
    (error.status === 404 ||
      (error.status === 403 && scopedIdentityReady && !hasScopedPermissions))

  const [unavailableEntityId, setUnavailableEntityId] = useState<string | null>(null)
  if (unavailable && unavailableEntityId !== entityId) setUnavailableEntityId(entityId)
  if (entity.isSuccess && unavailableEntityId === entityId) setUnavailableEntityId(null)

  return unavailable || (entityId != null && unavailableEntityId === entityId)
}
